import { useState } from 'react';
import {
  Loading,
  SectionSplit,
  useLoad,
} from '@affdice/portal-kit';
import { PlatformApi } from '../../client.ts';
import { t } from '../../messages.ts';
import { Failure } from '../shared.tsx';
import { OperatorAffiliatesPanel } from './sections/Affiliates.tsx';
import { OperatorBrandsPanel } from './sections/Brands.tsx';
import { OperatorPeriodsPanel } from './sections/Periods.tsx';
import { OperatorChangeRequestsPanel } from './sections/ChangeRequests.tsx';
import { OperatorAnalyticsPanel } from './sections/Dashboard.tsx';
import { OperatorReferralsPanel } from './sections/Referrals.tsx';
import { GoLiveSection } from './sections/GoLive.tsx';
import { VolumeTiersSection } from './sections/VolumeTiers.tsx';
import { PlatformUsageSection } from './sections/PlatformUsage.tsx';
import { PlatformInvoicesSection } from './sections/PlatformInvoices.tsx';
import { ProfileSection } from './sections/Profile.tsx';
import { OperatorTeamPanel } from './sections/Team.tsx';
import { OperatorCampaignsDealsPanel } from './sections/CampaignsDeals.tsx';
import { useAbilities } from '../abilities.tsx';
import { OperatorActions } from './OperatorActions.tsx';

/**
 * One operator, in the popup the Operators list opens: a section rail and,
 * beside it, whichever section is chosen. Each section is its own file under
 * sections/; what lives here is only what has to outlive switching between
 * them — the drafts, and which click-to-load panels have been opened.
 */
export function OperatorDetail({
  api,
  operatorId,
  owner,
  onChanged,
  onDeleted,
}: {
  api: PlatformApi;
  operatorId: string;
  /** The admin contact, from the list row — the detail read does not carry it. */
  owner: { owner_name: string | null; owner_email: string | null; owner_phone: string | null } | null;
  /** Told whenever this panel changes something the operators list shows. */
  onChanged?: () => void;
  /** Told once the operator is deleted, so whoever opened this can close it. */
  onDeleted: () => void;
}) {
  type DetailSection =
    'go-live' | 'tiers' | 'usage' | 'invoices' | 'affiliates' | 'brands' | 'periods'
    | 'referrals' | 'analytics' | 'change-requests' | 'team' | 'profile' | 'campaigns-deals';
  // Item 6 (third batch) — the dashboard opens first. Whoever opens an
  // operator wants to know how it is doing before they want its go-live
  // checklist, which mostly matters once, before it is live.
  const [section, setSection] = useState<DetailSection>('analytics');
  const [version, setVersion] = useState(0);
  // Item 3 (third batch) — an operator created before country was asked for.
  const [countryEdit, setCountryEdit] = useState<string | null>(null);
  // Batch 2 item 5 — the save used `window.alert`; the failure now shows
  // beside the form that made it, like every other write on this screen.
  const [countryError, setCountryError] = useState<unknown>(null);
  // Batch 2 item 5 — "Save band" and "Mark paid" dropped their `.catch`
  // entirely; both now land here so the error shows where the action was.
  const [actionError, setActionError] = useState<unknown>(null);
  const [fromUnits, setFromUnits] = useState('0');
  const [toUnits, setToUnits] = useState('');
  const [rateBps, setRateBps] = useState('0');
  // Item 6 (third batch) — which click-to-load panels have been opened. See
  // the comment where these are rendered for why it lives up here.
  const [opened, setOpened] = useState({
    affiliates: false, brands: false, periods: false, referrals: false, analytics: false,
    changeRequests: false, team: false, campaignsDeals: false,
  });
  const { can } = useAbilities();
  // Held here: saving re-reads the operator, which remounts the section.
  const [profileSaved, setProfileSaved] = useState(false);
  const loaded = useLoad(() => api.operatorDetail(operatorId), [operatorId, version]);
  const usage = useLoad(() => api.platformUsage(operatorId), [operatorId, version]);
  const invoices = useLoad(() => api.platformInvoices(operatorId), [operatorId, version]);
  const terms = useLoad(() => api.chargeTerms(operatorId), [operatorId, version]);
  if (loaded.state === 'loading') return <Loading rows={2} />;
  if (loaded.state === 'error') return <Failure error={loaded.error} />;

  return (
    <>
    <OperatorActions
      api={api}
      operatorId={operatorId}
      name={String(loaded.value.operator['display_name'] ?? '')}
      state={String(loaded.value.operator['state'] ?? 'PROSPECT')}
      refresh={() => setVersion((v) => v + 1)}
      onChanged={onChanged}
      onDeleted={onDeleted}
    />
    <SectionSplit
      ariaLabel={t('ops.detail.ariaLabel')}
      section={section}
      onSection={(next) => { setProfileSaved(false); setSection(next); }}
      groups={[{
        label: t('ops.detail.group'),
        items: [
          // Item 6 (third batch) — "Analytics" was the name of the data, not
          // of the screen; Dashboard is what it is, and it comes first.
          { id: 'analytics', label: t('ops.detail.dashboard') },
          { id: 'go-live', label: t('ops.detail.goLive') },
          { id: 'tiers', label: t('ops.detail.tiers') },
          { id: 'usage', label: t('ops.detail.usage') },
          { id: 'invoices', label: t('ops.detail.invoices') },
          { id: 'affiliates', label: t('ops.detail.affiliates') },
          { id: 'brands', label: t('ops.detail.brands') },
          // 3.9d — what a direct edit may switch on a campaign or a deal.
          { id: 'campaigns-deals', label: t('edit.cd.title') },
          // Beside Brands because that is what a period is scoped to, and
          // because the two are the console's only destructive reads: a brand
          // and a period are the things platform staff delete on an operator's
          // behalf when the operator's own console must not be able to.
          { id: 'periods', label: t('ops.detail.periods') },
          // §8.9 / §12.3 — read-only: AMS only reads a sub-affiliate network,
          // it never recruits into one or sets a recruiter's own rate.
          { id: 'referrals', label: t('ops.detail.subAffiliates') },
          // §3.9d — what support has asked this operator to change, beside the
          // things it would change. A proposal is not a platform action, so it
          // belongs where the operator relationship is read, not in a queue of
          // platform work.
          { id: 'change-requests', label: t('ops.detail.changes') },
          // 3.12 — the operator's own staff and brand-scoped staff.
          { id: 'team', label: t('ops.detail.team') },
          // Last: who this operator is, and the two things that change
          // whether it runs at all — suspend and delete.
          { id: 'profile', label: t('ops.detail.profile') },
        ],
      }]}
    >
        {section === 'go-live' ? (
          <GoLiveSection
            api={api}
            operatorId={operatorId}
            canWrite={can.edit}
            detail={loaded.value}
            country={{ countryEdit, setCountryEdit, countryError, setCountryError }}
            refresh={() => setVersion((v) => v + 1)}
            onChanged={onChanged}
          />
        ) : null}

        {section === 'tiers' ? (
          <VolumeTiersSection
            api={api}
            operatorId={operatorId}
            canWrite={can.configure}
            terms={terms}
            form={{ fromUnits, setFromUnits, toUnits, setToUnits, rateBps, setRateBps, actionError, setActionError }}
            refresh={() => setVersion((v) => v + 1)}
            onChanged={onChanged}
          />
        ) : null}

        {section === 'usage' ? (
          <PlatformUsageSection usage={usage} />
        ) : null}

        {section === 'invoices' ? (
          <PlatformInvoicesSection
            api={api}
            operatorId={operatorId}
            canWrite={can.configure}
            invoices={invoices}
            refresh={() => setVersion((v) => v + 1)}
          />
        ) : null}

        {/*
          Item 6 (third batch) — `opened` lives here, not inside each panel.
          These three are click-to-load (§3.9c logs per access, so nothing
          fetches until asked), and holding that flag inside a component the
          tab switch unmounts threw it away: leaving for another tab and
          coming back put the "View affiliates" button in front of somebody
          who had already pressed it. Held here it survives, and the panels
          stay conditionally rendered.
        */}
        {section === 'affiliates' ? (
          <OperatorAffiliatesPanel
            api={api}
            operatorId={operatorId}
            shown={opened.affiliates}
            onShow={() => setOpened((o) => ({ ...o, affiliates: true }))}
          />
        ) : null}

        {section === 'brands' ? (
          <OperatorBrandsPanel
            api={api}
            operatorId={operatorId}
            operatorState={String(loaded.value.operator['state'] ?? 'PROSPECT')}
            shown={opened.brands}
            onShow={() => setOpened((o) => ({ ...o, brands: true }))}
          />
        ) : null}

        {section === 'periods' ? (
          <OperatorPeriodsPanel
            api={api}
            operatorId={operatorId}
            shown={opened.periods}
            onShow={() => setOpened((o) => ({ ...o, periods: true }))}
            canWrite={can.purge}
          />
        ) : null}

        {section === 'referrals' ? (
          <OperatorReferralsPanel
            api={api}
            operatorId={operatorId}
            shown={opened.referrals}
            onShow={() => setOpened((o) => ({ ...o, referrals: true }))}
          />
        ) : null}

        {section === 'change-requests' ? (
          <OperatorChangeRequestsPanel
            api={api}
            operatorId={operatorId}
            shown={opened.changeRequests}
            onShow={() => setOpened((o) => ({ ...o, changeRequests: true }))}
            version={version}
          />
        ) : null}

        {section === 'team' ? (
          <OperatorTeamPanel
            api={api}
            operatorId={operatorId}
            shown={opened.team}
            onShow={() => setOpened((o) => ({ ...o, team: true }))}
          />
        ) : null}

        {section === 'profile' ? (
          <ProfileSection
            api={api}
            operatorId={operatorId}
            detail={loaded.value}
            owner={owner}
            saved={profileSaved}
            onSaved={() => { setProfileSaved(true); setVersion((v) => v + 1); onChanged?.(); }}
          />
        ) : null}

        {section === 'campaigns-deals' ? (
          <OperatorCampaignsDealsPanel
            api={api}
            operatorId={operatorId}
            operatorName={String(loaded.value.operator['display_name'] ?? '')}
            shown={opened.campaignsDeals}
            onShow={() => setOpened((o) => ({ ...o, campaignsDeals: true }))}
          />
        ) : null}

        {section === 'analytics' ? (
          <OperatorAnalyticsPanel
            api={api}
            operatorId={operatorId}
            shown={opened.analytics}
            onShow={() => setOpened((o) => ({ ...o, analytics: true }))}
          />
        ) : null}
    </SectionSplit>
    </>
  );
}
